import { rectSortingStrategy } from "@dnd-kit/sortable";
import {
  ActionIcon,
  Box,
  Button,
  Combobox,
  Group,
  ScrollArea,
  Text,
  Tooltip,
  useCombobox,
} from "@mantine/core";
import { IconPlus, IconX } from "@tabler/icons-react";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { API_ROUTE } from "../../const/apiRoute";
import { convertImageDomain } from "../../const/converhandle";
import { api } from "../../library/axios";
import { toSearchKey } from "./CategoryTreeSelect";
import { DragHandle, reorderList, SortableArea, useSortableItem } from "./HomeBlockShared";
import { BrandRef } from "./homeBlockConfig";
import classes from "./homeBlock.module.css";

export type BrandOption = { id: number; name: string; logo: string | null; searchKey: string };

// Logo trong Danh sách thương hiệu: image là đường dẫn, imageUrl là tên miền ảnh (giống màn Thương hiệu)
const logoOf = (item: any): string | null => {
  const image = typeof item?.image === "string" ? item.image.trim() : "";
  if (!image) return null;
  if (/^https?:\/\//i.test(image)) return convertImageDomain(image);
  const domain = typeof item?.imageUrl === "string" ? convertImageDomain(item.imageUrl.trim()) : "";
  return domain ? `${domain.replace(/\/$/, "")}/${image.replace(/^\//, "")}` : image;
};

// Tải Danh sách thương hiệu một lần cho cả màn; lỗi thì cho tải lại
let brandRequest: Promise<BrandOption[] | null> | null = null;

const loadBrands = () => {
  if (!brandRequest) {
    brandRequest = api
      .get(`${API_ROUTE.GET_LIST_BRAND}?Skip=0&Take=1000`)
      .then((res) => {
        const list = res.data?.data?.lists;
        if (!Array.isArray(list)) {
          brandRequest = null;
          return null;
        }
        return list
          .filter((item: any) => typeof item?.id === "number" && item?.name)
          .map(
            (item: any): BrandOption => ({
              id: item.id,
              name: String(item.name).trim(),
              logo: logoOf(item),
              searchKey: toSearchKey(String(item.name)),
            })
          );
      })
      .catch(() => {
        brandRequest = null;
        return null;
      });
  }
  return brandRequest;
};

export const useBrandList = () => {
  const [brands, setBrands] = useState<BrandOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadBrands().then((result) => {
      if (!alive) return;
      setBrands(result ?? []);
      setFailed(result === null);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [attempt]);

  const byId = useMemo(() => new Map(brands.map((b) => [b.id, b])), [brands]);
  return { brands, byId, loading, failed, retry: () => setAttempt((n) => n + 1) };
};

const BrandLogo = ({
  logo,
  name,
  large,
}: {
  logo: string | null;
  name: string;
  // Chip đã chọn: logo to hơn, không có logo thì hiện tên đủ thay vì 2 chữ đầu
  large?: boolean;
}) => {
  const [broken, setBroken] = useState(false);
  const hasLogo = !!logo && !broken;
  return (
    <span
      className={large ? `${classes.brandLogo} ${classes.brandLogoLarge}` : classes.brandLogo}
      aria-hidden
    >
      {hasLogo ? (
        <img src={logo as string} alt="" onError={() => setBroken(true)} />
      ) : (
        <span className={classes.brandLogoText}>{large ? name : name.slice(0, 2).toUpperCase()}</span>
      )}
    </span>
  );
};

const chipId = (brand: BrandRef) => `brand-${brand.id ?? brand.name}`;

const BrandChip = ({
  brand,
  option,
  unmatched,
  onRemove,
}: {
  brand: BrandRef;
  option?: BrandOption;
  unmatched: boolean;
  onRemove: () => void;
}) => {
  const { setNodeRef, style, handleProps } = useSortableItem(chipId(brand));
  const name = option?.name ?? brand.name;
  return (
    <Tooltip
      label={
        unmatched ? `Chưa khớp "${brand.name}" với Danh sách thương hiệu. Xoá rồi chọn lại.` : name
      }
      openDelay={300}
      withinPortal
    >
      <Group
        ref={setNodeRef}
        gap={6}
        wrap="nowrap"
        h={40}
        pl={6}
        pr={4}
        role="group"
        aria-label={`Thương hiệu ${name}`}
        className={unmatched ? `${classes.brandChip} ${classes.brandChipOff}` : classes.brandChip}
        style={style}
      >
        <DragHandle label={`Kéo để đổi thứ tự ${name}`} handleProps={handleProps} size={14} />
        <BrandLogo logo={option?.logo ?? null} name={name} large />
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          aria-label={`Bỏ thương hiệu ${name}`}
          onClick={onRemove}
        >
          <IconX size={14} />
        </ActionIcon>
      </Group>
    </Tooltip>
  );
};

type BrandStripEditorProps = {
  value: BrandRef[];
  onChange: Dispatch<SetStateAction<BrandRef[]>>;
};

// Chọn thương hiệu từ Danh sách thương hiệu: lưu id + tên, logo chỉ để xem (trang home lấy theo id)
export const BrandStripEditor = ({ value, onChange }: BrandStripEditorProps) => {
  const { brands, byId, loading, failed, retry } = useBrandList();
  const [search, setSearch] = useState("");
  const matchedRef = useRef(false);
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch("");
    },
    onDropdownOpen: () => combobox.focusSearchInput(),
  });

  // Dữ liệu mẫu chỉ có tên: khi tải xong danh sách thì khớp theo tên để lấy id
  useEffect(() => {
    if (matchedRef.current || loading || brands.length === 0) return;
    matchedRef.current = true;
    const byName = new Map(brands.map((b) => [toSearchKey(b.name), b]));
    onChange((list) =>
      list.map((ref) => {
        if (ref.id !== null) return ref;
        const found = byName.get(toSearchKey(ref.name));
        return found ? { id: found.id, name: found.name } : ref;
      })
    );
  }, [brands, loading, onChange]);

  const chosen = new Set(value.map((b) => b.id).filter((id): id is number => id !== null));
  const query = toSearchKey(search.trim());
  const options = brands
    .filter((b) => !chosen.has(b.id) && (!query || b.searchKey.includes(query)))
    .slice(0, 60);

  return (
    <Box>
      <SortableArea
        ids={value.map(chipId)}
        strategy={rectSortingStrategy}
        onReorder={(from, to) => onChange((list) => reorderList(list, from, to))}
      >
        <Group gap={8}>
          {value.map((brand) => (
            <BrandChip
              key={chipId(brand)}
              brand={brand}
              option={brand.id !== null ? byId.get(brand.id) : undefined}
              unmatched={!loading && !failed && brand.id === null}
              onRemove={() => onChange((list) => list.filter((b) => chipId(b) !== chipId(brand)))}
            />
          ))}
          <Combobox
            store={combobox}
            width={300}
            position="bottom-start"
            withinPortal
            onOptionSubmit={(val) => {
              const picked = byId.get(Number(val));
              if (picked) onChange((list) => [...list, { id: picked.id, name: picked.name }]);
              combobox.closeDropdown();
            }}
          >
            <Combobox.Target withAriaAttributes={false}>
              <Button
                size="xs"
                h={40}
                variant="default"
                leftSection={<IconPlus size={14} />}
                loading={loading}
                disabled={failed}
                onClick={() => combobox.toggleDropdown()}
              >
                Thêm thương hiệu
              </Button>
            </Combobox.Target>
            <Combobox.Dropdown>
              <Combobox.Search
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                placeholder="Tìm thương hiệu"
              />
              <Combobox.Options>
                <ScrollArea.Autosize mah={300} type="auto">
                  {options.length > 0 ? (
                    options.map((b) => (
                      <Combobox.Option value={String(b.id)} key={b.id}>
                        <Group gap={10} wrap="nowrap">
                          <BrandLogo logo={b.logo} name={b.name} />
                          <Text fz={13}>{b.name}</Text>
                        </Group>
                      </Combobox.Option>
                    ))
                  ) : (
                    <Combobox.Empty>Không có thương hiệu khớp</Combobox.Empty>
                  )}
                </ScrollArea.Autosize>
              </Combobox.Options>
            </Combobox.Dropdown>
          </Combobox>
        </Group>
      </SortableArea>
      {failed && (
        <Group gap={8} mt={10}>
          <Text fz={13} c="red.7">
            Không tải được Danh sách thương hiệu.
          </Text>
          <Button size="compact-xs" variant="subtle" onClick={retry}>
            Thử lại
          </Button>
        </Group>
      )}
      {value.length === 0 && !failed && (
        <Text fz={13} mt={10} className={classes.muted}>
          Chưa chọn thương hiệu. Dải logo sẽ ẩn trên web.
        </Text>
      )}
    </Box>
  );
};
